<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { hiddenInBranch } from '@/domain/catalog/catalog-filters';
  import { scheduleBadgeText } from '@/domain/catalog/catalog-status-text';
  import { scheduleSummary } from '@/domain/catalog/schedule-form';
  import { ICategory } from '@/interfaces/ICategory';

  /**
   * What the product inherits from its category (plan §5.12), only the
   * notes that apply: the category's schedule, hidden, POS only.
   */
  const props = defineProps<{
    category: ICategory;
    branchId?: number | null;
    branchName: string;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const notes = computed(() => {
    const { category } = props;
    const list: { key: string; text: string; tone: 'amber' | 'sky' }[] = [];
    if (!category.visible)
      list.push({
        key: 'hidden',
        text: t('menuPage.editor.inherit.hidden'),
        tone: 'amber',
      });
    else if (hiddenInBranch(category, props.branchId ?? null))
      list.push({
        key: 'hiddenHere',
        text: t('menuPage.editor.inherit.hiddenHere', {
          branch: props.branchName,
        }),
        tone: 'amber',
      });
    if (category.posOnly)
      list.push({
        key: 'posOnly',
        text: t('menuPage.editor.inherit.posOnly'),
        tone: 'sky',
      });
    const lines = scheduleSummary(category.hourRanges);
    if (lines.length)
      list.push({
        key: 'schedule',
        text:
          lines.length === 1
            ? t('menuPage.editor.inherit.schedule', {
                name: category.name,
                when: scheduleBadgeText(t, lines),
              })
            : t('menuPage.editor.inherit.schedules', { name: category.name }),
        tone: 'sky',
      });
    return list;
  });
</script>

<template>
  <div v-if="notes.length" class="v2-catalog-inheritance-notes">
    <p
      v-for="note in notes"
      :key="note.key"
      class="inherit-note"
      :class="`tone-${note.tone}`"
    >
      <i
        class="mdi note-icon"
        :class="
          note.tone === 'amber'
            ? 'mdi-eye-off-outline'
            : 'mdi-information-outline'
        "
      />
      <span>{{ note.text }}</span>
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-inheritance-notes {
    @apply tw-mb-3;

    .inherit-note {
      @apply tw-m-0 tw-flex tw-items-start tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-sm tw-leading-snug;

      & + .inherit-note {
        @apply tw-mt-2;
      }

      &.tone-amber {
        @apply tw-border-amber-200 tw-bg-amber-50 tw-text-amber-900;

        .note-icon {
          @apply tw-text-amber-600;
        }
      }

      &.tone-sky {
        @apply tw-border-sky-200 tw-bg-sky-50 tw-text-sky-900;

        .note-icon {
          @apply tw-text-sky-600;
        }
      }

      .note-icon {
        @apply tw-mr-2 tw-flex-none tw-text-base tw-leading-5;
      }
    }
  }
</style>
